import AppFormInput from '../../../components/ui/AppFormInput';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import apiClient from "../../../services/api-client";
import logo from '../../../assets/images/logo.png';
import {Toast} from 'primereact/toast';
import {useAuth} from '../components/use-auth';
import StorageHelper from '../../../utils/storage-helper';
import {useMountEffect} from 'primereact/hooks';

const OTP_RESEND_SECONDS = 60;

const Signin = () => {
    const history = useHistory();
    const toast = useRef(null);
    const {logout, login} = useAuth();

    const [mode, setMode] = useState('PASSWORD');
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');

    const [otpStep, setOtpStep] = useState(1); // 1 = request code, 2 = enter code
    const [otp, setOtp] = useState('');
    const [maskedPhone, setMaskedPhone] = useState('');
    const [resendIn, setResendIn] = useState(0);

    const [loading, setLoading] = useState(false);

    useMountEffect(() => {
        logout();
    });

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const t = setInterval(() => setResendIn((s) => s - 1), 1000);
        return () => clearInterval(t);
    }, [resendIn]);

    const showError = useCallback((detail, summary = 'Login failed') => {
        toast.current?.show({severity: 'error', summary, detail, life: 4500});
    }, []);

    const completeLogin = useCallback(
        (data) => {
            const body = data.responseBody || {};
            StorageHelper.set('token', data.accessToken);
            StorageHelper.set('isAuthenticated', true);
            StorageHelper.set('userRole', body.userRole);
            StorageHelper.set('userType', body.userType);
            StorageHelper.set('firstName', body.firstName);
            StorageHelper.set('emailAddress', body.emailAddress);
            StorageHelper.set('lastName', body.lastName);
            StorageHelper.set('userId', body.userId);
            StorageHelper.set('idnumber', body.idnumber);
            StorageHelper.set('userProfile', body);
            const user = {token: data.accessToken, data: body};
            login(user);
            history.push('/');
        },
        [history, login]
    );

    const apiError = useCallback(
        (error, fallback) => {
            const detail =
                error?.response?.data?.message ||
                (error?.response?.status === 401
                    ? 'Your session has expired. Please log in again.'
                    : fallback);
            showError(detail);
        },
        [showError]
    );

    const submitPassword = (e) => {
        e.preventDefault();
        if (!identifier.trim() || !password) {
            showError('Enter your ID number, policy number, email or phone, and your password.');
            return;
        }
        setLoading(true);
        apiClient
            .post('authenticate', {username: identifier.trim(), password})
            .then((res) => {
                setLoading(false);
                if (res.data.status === 'SUCCESS') {
                    if (!res.data.accessToken && res.data.message && res.data.message.includes('2FA')) {
                        toast.current?.show({
                            severity: 'info',
                            summary: '2FA Required',
                            detail: res.data.message,
                            life: 4500
                        });
                        setMode('2FA_CHALLENGE');
                        setOtp('');
                        setOtpStep(2);
                    } else {
                        completeLogin(res.data);
                    }
                } else {
                    showError(res.data.message);
                }
            })
            .catch((err) => {
                setLoading(false);
                apiError(err, 'An unexpected error occurred. Please try again later.');
            });
    };

    const requestOtp = (e) => {
        if (e) e.preventDefault();
        if (!identifier.trim()) {
            showError('Enter your ID number, policy number, email or phone number first.');
            return;
        }
        setLoading(true);
        apiClient
            .post('authenticate/otp/request', {identifier: identifier.trim()})
            .then((res) => {
                setLoading(false);
                const body = res.data.responseBody || {};
                setMaskedPhone(body.maskedPhone || '');
                setOtpStep(2);
                setResendIn(OTP_RESEND_SECONDS);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Code sent',
                    detail: `We sent a one-time PIN to ${body.maskedPhone || 'your registered mobile number'}.`,
                    life: 4500,
                });
            })
            .catch((err) => {
                setLoading(false);
                apiError(err, 'Could not send a one-time PIN. Please try again.');
            });
    };

    const verifyOtp = (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(otp.trim())) {
            showError('Enter the 6-digit PIN we sent to your phone.');
            return;
        }
        setLoading(true);
        apiClient
            .post('authenticate/otp/verify', {identifier: identifier.trim(), otp: otp.trim()})
            .then((res) => {
                setLoading(false);
                if (res.data.status === 'SUCCESS') completeLogin(res.data);
                else showError(res.data.message);
            })
            .catch((err) => {
                setLoading(false);
                apiError(err, 'Could not verify the PIN. Please try again.');
            });
    };

    const verify2FAOtp = (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(otp.trim())) {
            showError('Enter the 6-digit PIN we sent to your phone and email.');
            return;
        }
        setLoading(true);
        apiClient
            .post('authenticate/2fa/verify-otp', {identifier: identifier.trim(), otp: otp.trim()})
            .then((res) => {
                setLoading(false);
                if (res.data.status === 'SUCCESS') completeLogin(res.data);
                else showError(res.data.message);
            })
            .catch((err) => {
                setLoading(false);
                apiError(err, 'Could not verify the 2FA PIN. Please try again.');
            });
    };

    const switchMode = (value) => {
        if (value == null) return;
        setMode(value);
        setOtpStep(1);
        setOtp('');
        setPassword('');
    };

    return (
        <>
            <Toast ref={toast}/>
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center mb-8 flex flex-col items-center">
                        <Link to={'/landing'} className="flex items-center justify-center space-x-3 mb-6">
                            <img className="h-10 w-auto" src={logo} alt="Doves Life Assurance"/>
                            <span
                                className="bg-[#EBF5FF] text-[#135A9C] font-black text-[10px] px-3 py-1 rounded-full uppercase tracking-widest">Life Assurance Self-Service Portal</span>
                        </Link>
                        <h1 className="text-2xl font-extrabold text-slate-800 mt-2 tracking-tight">Sign in to manage
                            your policy</h1>
                        <p className="mt-2 text-sm text-slate-500">
                            Use your National ID, policy number, email or phone number.
                        </p>
                    </div>

                    <div
                        className="bg-white py-8 px-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:rounded-3xl sm:px-10 border border-slate-100">
                        {loading ? (
                            <div className="animate-pulse">
                                <div className="flex mb-4 items-center">
                                    <div className="w-16 h-16 bg-slate-200 rounded-full mr-4"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="h-32 bg-slate-200 rounded w-full mb-4"></div>
                            </div>
                        ) : (
                            <div>
                                <div className="text-center mb-3">
                                    <span className="text-xs font-bold text-slate-700">Sign in with</span>
                                </div>

                                {mode === '2FA_CHALLENGE' && (
                                    <form onSubmit={verify2FAOtp} className="space-y-5">
                                        <AppFormInput
                                            label="Enter the 6-digit 2FA PIN"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            inputClassName="text-center tracking-[0.5em] font-mono font-bold"
                                            placeholder="------"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            autoComplete="one-time-code"
                                            autoFocus
                                            hint="Sent to your registered email and mobile number."
                                            required
                                        />

                                        <button type="submit"
                                                className="btn w-full rounded-full font-bold mt-2 bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none">
                                            Verify &amp; sign in
                                        </button>

                                        <div className="flex justify-center mt-4">
                                            <button
                                                type="button"
                                                className="text-xs text-[#135A9C] hover:underline font-bold"
                                                onClick={() => {
                                                    setMode('PASSWORD');
                                                    setOtp('');
                                                    setPassword('');
                                                }}
                                            >
                                                Back to Login
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {mode === 'PASSWORD' && (
                                    <form onSubmit={submitPassword} className="space-y-4">
                                        <AppFormInput
                                            label="National ID / Policy number / Email / Phone"
                                            type="text"
                                            placeholder="e.g. 63-1234567A00 or POL123456"
                                            value={identifier}
                                            onChange={(e) => setIdentifier(e.target.value)}
                                            autoComplete="username"
                                            required
                                        />

                                        <AppFormInput
                                            label="Password"
                                            type="password"
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="current-password"
                                            required
                                        />

                                        <div className="flex items-center justify-between mt-1 pt-2">
                                            <div className="flex items-center">
                                                <input type="checkbox" id="rememberMe"
                                                       className="checkbox checkbox-xs border-slate-300 checked:border-[#135A9C] checked:bg-[#135A9C] rounded"/>
                                                <label htmlFor="rememberMe"
                                                       className="ml-2 block text-xs text-slate-500 cursor-pointer">
                                                    Remember me
                                                </label>
                                            </div>
                                            <div className="text-xs">
                                                <Link to={'/reset'}
                                                      className="font-bold text-[#135A9C] hover:underline">
                                                    Forgot password?
                                                </Link>
                                            </div>
                                        </div>

                                        <button type="submit"
                                                className="btn w-full rounded-full font-bold mt-6 bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none">
                                            Sign In <i className="icofont-arrow-right ml-1"></i>
                                        </button>
                                    </form>
                                )}

                                {mode === 'OTP' && otpStep === 1 && (
                                    <form onSubmit={requestOtp} className="space-y-4">
                                        <AppFormInput
                                            label="National ID / Policy number / Email / Phone"
                                            type="text"
                                            placeholder="e.g. 63-1234567A00 or POL123456"
                                            value={identifier}
                                            onChange={(e) => setIdentifier(e.target.value)}
                                            autoComplete="username"
                                            hint="We&apos;ll send a 6-digit PIN to the mobile number registered on your policy."
                                            required
                                        />

                                        <button type="submit"
                                                className="btn w-full rounded-full font-bold mt-2 bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none">
                                            Send me a code
                                        </button>
                                    </form>
                                )}

                                {mode === 'OTP' && otpStep === 2 && (
                                    <form onSubmit={verifyOtp} className="space-y-5">
                                        <AppFormInput
                                            label="Enter the 6-digit PIN"
                                            type="text"
                                            inputMode="numeric"
                                            pattern="\\d{6}"
                                            maxLength={6}
                                            inputClassName="text-center tracking-[0.5em] font-mono font-bold"
                                            placeholder="------"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                            autoComplete="one-time-code"
                                            autoFocus
                                            hint="Sent to {maskedPhone || 'your registered mobile number'}."
                                            required
                                        />

                                        <button type="submit"
                                                className="btn w-full rounded-full font-bold mt-2 bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none">
                                            Verify &amp; sign in
                                        </button>

                                        <div className="flex justify-between items-center text-xs mt-4">
                                            <button
                                                type="button"
                                                className="text-[#135A9C] hover:underline font-bold"
                                                onClick={() => {
                                                    setOtpStep(1);
                                                    setOtp('');
                                                }}
                                            >
                                                Use a different account
                                            </button>
                                            <button
                                                type="button"
                                                className={`${resendIn > 0 ? 'text-slate-400' : 'text-[#135A9C] hover:underline'} font-bold`}
                                                disabled={resendIn > 0}
                                                onClick={() => requestOtp()}
                                            >
                                                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}

                        <div className="mt-8 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
                            New here? <Link to={'/landing'} className="font-bold text-[#135A9C] hover:underline ml-1">Create
                            an account</Link>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default Signin;
